import { useMemo } from 'react'
import { usePendingEntries } from '@/hooks/usePendingEntries'
import { getNetWorkSeconds, secondsToHours, sumNetWorkSeconds } from '@/lib/dates'
import { getPeriodBounds, getPeriodInfo, getPreviousPeriod } from '@/lib/periods'
import type { PeriodConfig } from '@/types/periods'
import type { TimeEntriesExpanded } from '@/types/timeTracking'

export interface StackedHoursItem {
    label: string
    approved: number
    unapproved: number
}

interface StatusBreakdown {
    status: string
    count: number
    hours: number
}

function getEntryHours(entry: TimeEntriesExpanded) {
    return secondsToHours(getNetWorkSeconds(entry))
}

interface AccumulatorEntry {
    label: string
    approved: number
    unapproved: number
}

function buildStatusBreakdown(entries: TimeEntriesExpanded[]): StatusBreakdown[] {
    const statusMap = new Map<string, { count: number; hours: number }>()
    for (const e of entries) {
        const status = e.status || 'active'
        const existing = statusMap.get(status)
        const hours = getEntryHours(e)
        if (existing) {
            existing.count++
            existing.hours += hours
        } else {
            statusMap.set(status, { count: 1, hours })
        }
    }
    return Array.from(statusMap.entries()).map(([status, { count, hours }]) => ({
        status,
        count,
        hours,
    }))
}

function buildStackedData(
    entries: TimeEntriesExpanded[],
    getKey: (e: TimeEntriesExpanded) => string,
    getLabel: (e: TimeEntriesExpanded) => string
): StackedHoursItem[] {
    const map = new Map<string, AccumulatorEntry>()
    for (const e of entries) {
        const key = getKey(e)
        const hours = getEntryHours(e)
        const isApproved = e.status === 'approved'
        const existing = map.get(key)
        if (existing) {
            if (isApproved) existing.approved += hours
            else existing.unapproved += hours
        } else {
            map.set(key, {
                label: getLabel(e),
                approved: isApproved ? hours : 0,
                unapproved: isApproved ? 0 : hours,
            })
        }
    }
    return Array.from(map.values())
        .map(item => ({
            ...item,
            approved: Math.max(0, item.approved),
            unapproved: Math.max(0, item.unapproved),
        }))
        .sort((a, b) => b.approved + b.unapproved - (a.approved + a.unapproved))
}

export function useAnalyticsData(periodDate: Date, periodConfig: PeriodConfig, enabled = true) {
    const currentBounds = getPeriodBounds(periodDate, periodConfig)
    const periodInfo = getPeriodInfo(periodDate, periodConfig)

    const prevDate = getPreviousPeriod(periodDate, periodConfig)
    const prevBounds = getPeriodBounds(prevDate, periodConfig)

    const { entries, isLoading: isLoadingCurrent } = usePendingEntries({
        startDate: currentBounds.start,
        endDate: currentBounds.end,
        enabled,
    })

    const { entries: prevEntries, isLoading: isLoadingPrevious } = usePendingEntries({
        startDate: prevBounds.start,
        endDate: prevBounds.end,
        enabled,
    })

    const analytics = useMemo(() => {
        const totalHours = secondsToHours(sumNetWorkSeconds(entries))
        const totalEntries = entries.length

        const userSet = new Set<string>()
        for (const e of entries) {
            if (e.user) userSet.add(e.user)
        }
        const activeUsers = userSet.size
        const avgHoursPerUser = activeUsers > 0 ? totalHours / activeUsers : 0

        const previousPeriodHours = secondsToHours(sumNetWorkSeconds(prevEntries))
        const hoursChangePercent =
            previousPeriodHours > 0
                ? ((totalHours - previousPeriodHours) / previousPeriodHours) * 100
                : 0

        const hoursByUser = buildStackedData(
            entries,
            e => e.user || 'unknown',
            e => e.expand?.user?.name || 'Unknown'
        )

        const hoursByJob = buildStackedData(
            entries,
            e => e.job || 'unassigned',
            e => {
                const jobName = e.expand?.job?.name || 'Unassigned'
                const customerName = e.expand?.customer?.name
                return customerName ? `${jobName} (${customerName})` : jobName
            }
        )

        const hoursByCustomer = buildStackedData(
            entries,
            e => e.expand?.customer?.id || 'none',
            e => e.expand?.customer?.name || 'No Customer'
        )

        const statusBreakdown = buildStatusBreakdown(entries)

        return {
            totalHours,
            totalEntries,
            activeUsers,
            avgHoursPerUser,
            previousPeriodHours,
            hoursChangePercent,
            hoursByUser,
            hoursByJob,
            hoursByCustomer,
            statusBreakdown,
        }
    }, [entries, prevEntries])

    return {
        ...analytics,
        periodLabel: periodInfo.label,
        isLoading: isLoadingCurrent || isLoadingPrevious,
        hasData: entries.length > 0,
    }
}
